import {
  Button,
  Container,
  Group,
  Table,
  Text,
  ThemeIcon,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { openContextModal } from "@mantine/modals";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import roundrobin from "roundrobin";
import { trackEvent } from "../../common/analytics";
import { createMatch } from "../../common/api";
import { Board } from "../../common/boards/board.schema";
import { useIsEventReferee } from "../../common/events/event.hooks";
import {
  Event,
  Pool,
  PoolMember,
  TournamentType,
} from "../../common/events/event.schema";
import { Swords } from "../../common/icons/swords";
import { Match } from "../../common/matches/match.schema";
import { Member } from "../../common/members/member.schema";
import { useAuthStore } from "../../common/stores/auth.store";
import { useMatchStore } from "../../common/stores/match.store";
import { isProd } from "../../common/util";
import MemberDisplay from "../members/MemberDisplay";
import { getHasTeams, getIsEndless, getMaxPoints } from "./util";
export interface PoolMatchup {
  a: PoolMember;
  b: PoolMember;
  match?: Match;
  count: number;
  index: number;
}
const POOL_ORDERS = [
  {
    count: 2,
    order: [[1, 2]],
  },
  {
    count: 3,
    order: [
      [1, 2],
      [2, 3],
      [1, 3],
    ],
  },
  {
    count: 4,
    order: [
      [1, 4],
      [2, 3],
      [1, 3],
      [2, 4],
      [3, 4],
      [1, 2],
    ],
  },
  {
    count: 5,
    order: [
      [1, 2],
      [3, 4],
      [5, 1],
      [2, 3],
      [5, 4],
      [1, 3],
      [2, 5],
      [4, 1],
      [3, 5],
      [4, 2],
    ],
  },
  {
    count: 6,
    order: [
      [1, 2],
      [4, 5],
      [2, 3],
      [5, 6],
      [3, 1],
      [6, 4],
      [2, 5],
      [1, 4],
      [5, 3],
      [1, 6],
      [4, 2],
      [3, 6],
      [5, 1],
      [3, 4],
      [6, 2],
    ],
  },
  {
    count: 7,
    order: [
      [1, 4],
      [2, 5],
      [3, 6],
      [7, 1],
      [5, 4],
      [2, 3],
      [6, 7],
      [5, 1],
      [4, 3],
      [6, 2],
      [5, 7],
      [3, 1],
      [4, 6],
      [7, 2],
      [3, 5],
      [1, 6],
      [2, 4],
      [7, 3],
      [6, 5],
      [1, 2],
      [4, 7],
    ],
  },
  {
    count: 8,
    order: [
      [2, 3],
      [1, 5],
      [7, 4],
      [6, 8],
      [1, 2],
      [3, 4],
      [5, 6],
      [8, 7],
      [4, 1],
      [5, 2],
      [8, 3],
      [6, 7],
      [4, 2],
      [8, 1],
      [7, 5],
      [3, 6],
      [2, 8],
      [5, 4],
      [6, 1],
      [3, 7],
      [4, 8],
      [2, 6],
      [3, 5],
      [1, 7],
      [4, 6],
      [8, 5],
      [7, 2],
      [1, 3],
    ],
  },
];
export function getMatchups(
  event: Event,
  pool: Pool,
  includeComplete?: boolean
) {
  const matches = event.matches as Match[];
  const activeMembers = pool.members.filter((pm) => !pm.withdrawn);
  let flatRounds: [PoolMember, PoolMember][] | undefined;
  if (activeMembers.length < 9) {
    const order = POOL_ORDERS.find((o) => o.count === activeMembers.length);
    flatRounds = [];
    flatRounds = order?.order.map((o) => {
      const a = activeMembers[o[0] - 1];
      const b = activeMembers[o[1] - 1];
      return [a, b];
    });
  }
  if (!flatRounds) {
    const rounds = roundrobin(activeMembers.length, activeMembers) as [
      PoolMember,
      PoolMember
    ][][];
    //   console.log("rounds :", rounds);
    flatRounds = rounds.flat().filter((r) => r.length === 2);
  }
  // console.log("flatRounds :", flatRounds);
  if (!flatRounds) return [];
  // console.log("flatRounds :", flatRounds);
  let matchups: PoolMatchup[] = flatRounds.map((m, index) => {
    const a = m[0] as PoolMember;
    const b = (m as any)[1] as unknown as PoolMember;
    const matchup: PoolMatchup = {
      a,
      b,
      count: matches.filter(
        (m) =>
          (m.members as string[]).includes(a.member) &&
          (m.members as string[]).includes(b.member)
      ).length,
      index,
    };
    if (!getIsEndless(event)) {
      matchup.match = matches?.find((ma) => {
        const members = [ma.winner, ma.loser];
        return (
          members.includes(matchup.a.member) &&
          members.includes(matchup.b.member)
        );
      });
    }
    return matchup;
  });
  if (!includeComplete) matchups = matchups.filter((matchup) => !matchup.match);
  if (getIsEndless(event)) {
    matchups.sort((a, b) => {
      if (a.count < b.count) return -1;
      if (a.count > b.count) return 1;
      if (a.index < b.index) return -1;
      if (a.index > b.index) return 1;
      return 0;
    });
  }
  // console.log("matchups :", matchups);
  return matchups;
}
const NextMatches = ({
  board,
  event,
  pool,
}: {
  board: Board;
  event: Event;
  pool: Pool;
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const user = useAuthStore((s) => s.user);
  const matches = event.matches as Match[];
  let members = event.members as Member[];
  if (event.type === TournamentType.TEAM_POOLS_ELIMINATION)
    members = event.teams as Member[];
  const queryClient = useQueryClient();
  const resetFormValues = useMatchStore((s) => s.resetFormValues);
  const colorScheme = useComputedColorScheme();
  const isRef = useIsEventReferee();
  // console.log("flatRounds :", flatRounds);
  const endless = getIsEndless(event);
  const matchups: PoolMatchup[] = getMatchups(event, pool, endless);
  // console.log("matchups got :", matchups);

  if (!members || !matches || !matchups.length) return <></>;
  //   console.log("matchups filtered :", matchups);
  const openMatch = (matchup: PoolMatchup) => {
    resetFormValues();
    const memberId = matchup.a.member;
    const poolMembers = pool.members
      .map((m) => m.member)
      .filter(
        (o) =>
          !matches.find((m) => {
            const ms = [m.winner, m.loser];
            return ms.indexOf(o) > -1 && ms.indexOf(memberId) > -1 && m.pool;
          })
      );
    trackEvent("open_modal_add_match", { board: board?._id });
    openContextModal({
      title: t("addMatch"),
      modal: "addMatch",
      fullScreen: true,
      onClose: () => resetFormValues(),
      innerProps: {
        memberId,
        opponentId: matchup.b.member,
        poolMembers,
        event: event,
        requirePoints: true,
        pool: true,
        maxPoints: getMaxPoints(event, board),
        hasTeams: getHasTeams(event),
      },
    });
  };
  const autoComplete = async () => {
    const toComplete = matchups.filter((m) => !m.match);
    const next = async () => {
      // const a =
      const matchup = toComplete.pop();
      if (!matchup) return;
      const a = matchup.a.member;
      const b = matchup.b.member;
      const w = Math.random() > 0.5;
      await createMatch({
        members: [a, b],
        winner: w ? a : b,
        loser: w ? b : a,
        tournament: event._id,
        board: board._id,
        winnerPoints:
          event.type === TournamentType.TEAM_POOLS_ELIMINATION ? 45 : 5,
        loserPoints: Math.round(
          Math.random() *
            (event.type === TournamentType.TEAM_POOLS_ELIMINATION ? 44 : 4)
        ),
      });
      if (toComplete.length) {
        next();
      } else {
        queryClient.refetchQueries();
      }
    };
    next();
  };
  //   console.log("nextMatchup :", nextMatchup);
  const nextUps: PoolMatchup[] = matchups.slice(0, 4);
  // nextUps = nextUps.slice(0, 4);
  // console.log("nextUps :", nextUps);
  const maxName = 20;
  const maxNameSmall = 15;
  return (
    <>
      {!pool.complete && (
        <Container size="xs" p={0} mb={10} pt={10}>
          {user?.admin && !isProd && !endless && (
            <Group justify="right">
              <Button onClick={() => autoComplete()} variant="subtle" size="xs">
                Autocomplete pool
              </Button>
            </Group>
          )}
          {!endless && (
            <Text ta="center" size="xs">
              {nextUps.length === 1 ? "Last match" : "Next matches"}
            </Text>
          )}
          {endless && (
            <Text ta="center" size="xs">
              {nextUps.length === 1
                ? "Next suggested match"
                : "Next suggested matches"}
            </Text>
          )}
          <Table
            pt={0}
            cellPadding={5}
            style={{
              borderCollapse: "separate",
              borderSpacing: "0 10px",
            }}
          >
            <Table.Tbody>
              {nextUps.map((matchup, i) => {
                const a = members.find((m) => m._id === matchup.a.member);
                // console.log("a :", a);
                const b = members.find((m) => m._id === matchup.b.member);
                // console.log("b :", b);
                if (!a || !b) return null;
                // console.log("user :", user);
                const self =
                  user && (user._id === a.user || user._id === b.user);
                const addSelf = self && board.ownMatches;
                // console.log("self :", self);
                return (
                  <Table.Tr
                    style={{
                      backgroundColor:
                        colorScheme === "dark"
                          ? theme.colors.dark[5]
                          : theme.colors.gray[0],
                    }}
                    key={matchup.a.member + matchup.b.member}
                    onClick={() => openMatch(matchup)}
                  >
                    <Table.Td align="right">
                      <MemberDisplay
                        avatar
                        position="right"
                        size="xs"
                        member={a}
                        max={maxName}
                        maxXS={maxNameSmall}
                      />
                    </Table.Td>
                    <Table.Td width={50} align="center">
                      <ThemeIcon size="xs" variant="light">
                        <Text fz={10}>{t("vs")}</Text>
                      </ThemeIcon>
                    </Table.Td>
                    <Table.Td>
                      <MemberDisplay
                        avatar
                        size="xs"
                        member={b}
                        max={maxName}
                        maxXS={maxNameSmall}
                      />
                    </Table.Td>
                    {(isRef || addSelf) && (
                      <>
                        <Table.Td visibleFrom="xs" width={100}>
                          <Button
                            size="xs"
                            variant={i === 0 ? "filled" : "default"}
                            leftSection={<Swords />}
                          >
                            {t("Result")}
                          </Button>
                        </Table.Td>
                        <Table.Td hiddenFrom="xs" width={30}>
                          <ThemeIcon variant={i > 0 ? "light" : "filled"}>
                            <Swords />
                          </ThemeIcon>
                        </Table.Td>
                      </>
                    )}
                    {!(isRef || addSelf) && <Table.Td></Table.Td>}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Container>
      )}
    </>
  );
};
export default NextMatches;
